/* ==========================================================================
   mx-motion.js — micro-animations du thème (chantier motion).

   Chargé UNE fois dans theme.liquid (defer). Opt-in par attribut : le HTML
   déclare l'intention, comme une classe mx-*.

   Périmètre actuel — l'APPUI RESSORT :
     <a class="mx-btn mx-btn--primary" data-mx-press>…</a>
   L'appui se tient tant qu'on presse (descente ease-out), le relâchement
   rebondit : la courbe dépasse la taille de repos puis s'y pose.

   Pourquoi du JS : le CSS tient l'enfoncement (:active est un état), mais il
   ne sait pas distinguer les deux chemins qui mènent au même état d'arrivée —
   après un relâchement on veut un ressort lent, après une simple entrée de
   souris un survol vif. D'où le ressort joué à la main, au relâchement.

   Les animations sont ADDITIVES (composite: 'add') : de purs multiplicateurs
   qui s'éteignent sur l'identité. Elles se composent avec n'importe quel
   survol (roll, inversion…) au lieu de l'écraser, et ne laissent aucun
   transform inline résiduel.

   Réglages : tokens --mx-press / --mx-duration-* / --mx-curve-* de
   variable-animations.liquid, surchargeables par élément via
   data-mx-press="0.9" data-mx-spring-ms="800" data-mx-curve="…".

   prefers-reduced-motion coupe tout. Sans JS : rien ne change, le bouton
   reste un bouton.
   ========================================================================== */
(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  /* Tokens lus une fois au chargement (le snippet variable-animations est
     rendu dans le <head>, ce script est en defer : ils existent déjà). */
  const rootStyle = getComputedStyle(document.documentElement);
  const token = (name, fallback) => rootStyle.getPropertyValue(name).trim() || fallback;
  const toMs = (value, fallback) => {
    const n = parseFloat(value);
    if (!isFinite(n)) return fallback;
    return value.includes('ms') || !value.includes('s') ? n : n * 1000;
  };

  const PRESS = parseFloat(token('--mx-press', '0.96')) || 0.96;
  const DOWN_MS = toMs(token('--mx-duration-press', '140ms'), 140);
  const SPRING_MS = toMs(token('--mx-duration-spring', '620ms'), 620);
  const CURVE_DOWN = token('--mx-curve-press', 'cubic-bezier(0.22, 1, 0.36, 1)');
  const CURVE_SPRING = token('--mx-curve-spring', 'cubic-bezier(0.3, 1.9, 0.44, 1)');

  /* Surcharges locales par attribut, pour les cas particuliers. */
  const cfg = (el) => ({
    press: parseFloat(el.dataset.mxPress) || PRESS,
    springMs: parseFloat(el.dataset.mxSpringMs) || SPRING_MS,
    curve: el.dataset.mxCurve || CURVE_SPRING,
  });

  const held = new WeakMap();

  const down = (el) => {
    if (!el.animate) return;
    if (el._mxUp) {
      el._mxUp.cancel();
      el._mxUp = null;
    }
    if (held.get(el)) return;
    held.set(
      el,
      el.animate([{ transform: 'scale(1)' }, { transform: `scale(${cfg(el).press})` }], {
        duration: DOWN_MS,
        easing: CURVE_DOWN,
        fill: 'forwards',
        composite: 'add',
      })
    );
  };

  const up = (el) => {
    const pressed = held.get(el);
    if (!pressed) return;
    held.delete(el);
    pressed.cancel();
    if (!el.animate) return;
    const c = cfg(el);
    el._mxUp = el.animate([{ transform: `scale(${c.press})` }, { transform: 'scale(1)' }], {
      duration: c.springMs,
      easing: c.curve,
      composite: 'add',
    });
  };

  const target = (t) => (t && t.closest ? t.closest('[data-mx-press]') : null);

  /* Délégation sur document : couvre aussi le contenu injecté après coup
     (éditeur de thème, quick-add, drawers) sans rescanner quoi que ce soit. */
  document.addEventListener('pointerdown', (event) => {
    const el = target(event.target);
    if (el) down(el);
  }, true); /* capture : insensible aux stopPropagation des composants */

  for (const type of ['pointerup', 'pointercancel']) {
    document.addEventListener(type, (event) => {
      const el = target(event.target);
      if (el) up(el);
      /* relâché ailleurs : aucun bouton ne doit rester écrasé */ else
        document.querySelectorAll('[data-mx-press]').forEach(up);
    }, true);
  }

  document.addEventListener('pointerout', (event) => {
    const el = target(event.target);
    if (el && !el.contains(event.relatedTarget)) up(el);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = target(event.target);
    if (el) down(el);
  }, true);

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = target(event.target);
    if (el) up(el);
  }, true);

  /* ---- Roll du libellé sans markup : data-mx-roll -------------------------
     L'effet CSS (.mx-btn--roll) demande le libellé en double dans le markup.
     Ici le runtime fabrique cette structure tout seul :

       <a class="mx-btn mx-btn--primary" data-mx-roll>Découvrir</a>

     Uniquement pour un libellé en texte simple : un bouton qui contient déjà
     des éléments (icône, structure…) est laissé tel quel — pour ces cas-là,
     écrire le markup .mx-roll à la main. Sans JS ou en reduced-motion, le
     bouton reste un bouton à libellé simple : rien ne casse. */
  const rollify = (root) => {
    for (const el of (root || document).querySelectorAll('[data-mx-roll]')) {
      /* La STRUCTURE fait foi, pas la classe : un re-rendu (hydratation du
         header, morphing) peut rayer les spans en laissant la classe. */
      if (el.querySelector(':scope > .mx-roll')) continue;
      if (el.children.length > 0) continue; /* markup étranger : on ne touche pas */

      const label = el.textContent.trim();
      if (!label) continue;

      const wrap = document.createElement('span');
      wrap.className = 'mx-roll';
      const visible = document.createElement('span');
      visible.textContent = label;
      const ghost = document.createElement('span');
      ghost.textContent = label;
      ghost.setAttribute('aria-hidden', 'true');
      wrap.append(visible, ghost);

      el.textContent = '';
      el.append(wrap);
      el.classList.add('mx-btn--roll');
    }
  };

  const boot = () => rollify(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* L'éditeur de thème re-rend les sections : on refabrique dedans. */
  document.addEventListener('shopify:section:load', (event) => rollify(event.target));

  /* Le DOM bouge sans nous prévenir — hot reload de `theme dev`, quick-add,
     contenu injecté dans les drawers : un observateur refabrique le roll de
     tout [data-mx-roll] qui apparaît. Coalescé en microtâche : une rafale de
     mutations = un seul passage. rollify saute déjà les éléments traités
     (classe mx-btn--roll posée), le repassage est bon marché. */
  let rollScanQueued = false;
  const queueRollScan = () => {
    if (rollScanQueued) return;
    rollScanQueued = true;
    queueMicrotask(() => {
      rollScanQueued = false;
      rollify(document);
    });
  };

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      /* un morph peut VIDER un bouton roll (retrait des spans) sans rien
         ajouter d'identifiable : si la cible de la mutation est un
         [data-mx-roll], on repasse aussi. */
      const host = mutation.target;
      if (host.nodeType === 1 && host.closest && host.closest('[data-mx-roll]')) {
        queueRollScan();
        return;
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute('data-mx-roll') || node.querySelector('[data-mx-roll]')) {
          queueRollScan();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
